import React from "react";
import { CandybarItemConfigType, CandybarItemOptionType, CandybarItemType } from "./types";
import { centsToCurrencyString } from "../../../utils/numbers";
import styled from "styled-components";
import CandybarConfigPanel from "./CandybarConfigPanel";
import CandybarItemOption from "./CandybarItemOption";
import CandybarSubConfigs from "./CandybarSubConfigs";

const List = styled.ul`
    list-style-type: none;
    margin: 1em 0;
    padding: 0;
`;

interface Props {
    config: CandybarItemConfigType,
    isSubConfig: boolean,
    unitConfigs: object|null,
    onOptionSelect: Function
}

function CandybarItemConfig(props:Props) {
    const { config, unitConfigs } = props;
    const ref = React.useRef<HTMLDivElement>(null);
    const unitConfig = unitConfigs && (config.id in unitConfigs) ? unitConfigs[config.id] : null;
    const multiple = config.max > 1;
    const summary = getSummaryText(config, unitConfigs);

    function onOptionSelect(config, option, selected) {
        props.onOptionSelect(config, option, selected);
        if (ref.current && ref.current.parentElement) {
            const thisPanel = ref.current.parentElement.parentElement as HTMLDivElement;
            const allPanels = document.querySelectorAll('.candybar-config-panel');
            const allPanelsArray = Array.from(allPanels);
            const nextPanelIndex = allPanelsArray.indexOf(thisPanel) + 1;
            const nextPanelInDom = allPanelsArray[nextPanelIndex] as HTMLDivElement;
            const container = ref.current.closest('.floating-window') as HTMLDivElement;
            if (nextPanelInDom && container) {
                const yOffset = -80;
                const y = nextPanelInDom.offsetTop + yOffset;
                container.scrollTo({ top: y, behavior: 'smooth' });
            }
        }
    }

    return <CandybarConfigPanel
        bigTitle={!props.isSubConfig}
        name={config.name}
        summary={summary}
        mandatory={config.min > 0}
    >
        <div ref={ref}>
            <List>
                {config.options.map(option => <CandybarItemOption 
                    config={config}
                    multiple={multiple}
                    option={option}
                    onOptionSelect={onOptionSelect}
                    unitConfig={unitConfig}
                    unitConfigs={unitConfigs}
                />)}
            </List>
        </div>
        {<CandybarSubConfigs
            config={config}
            unitConfigs={unitConfigs}
            onOptionSelect={props.onOptionSelect}
        />}
    </CandybarConfigPanel>;
}

function getSummaryText(config: CandybarItemConfigType, unitConfigs): string {
    const placeholder = '';
    const unitConfig = unitConfigs && (config.id in unitConfigs) ? unitConfigs[config.id] : null;

    if (!unitConfig) {
        return placeholder;
    }

    const selectedOptions: string[] = Object.keys(unitConfig).reduce((carry, key) => {
        carry = carry.concat(unitConfig[key]);
        return carry;
    }, []);

    if (!selectedOptions.length) {
        return placeholder;
    }
    
    return config.options
        .filter(option => selectedOptions.indexOf(option.id) > -1)
        .map(option => getOptionSummaryText(option, unitConfigs))
        .join(', ');
}

function getOptionSummaryText(option: CandybarItemOptionType, unitConfigs): string {
    let result = option.name + (option.price > 0 ? ` + ${centsToCurrencyString(option.price)}` : '')
    const subConfigsSummary = option.subConfigs.map(subConfig => getSummaryText(subConfig, unitConfigs)).filter(str => str.length).join(', ');

    if (subConfigsSummary) {
        result += ' > ' + subConfigsSummary;
    }

    return result;
}

export default CandybarItemConfig;